/**
 * Grade catalog — typed wrapper over `GET /v1/twin/grades` plus its co-located
 * React Query hook (Phase-2 Wave-0 recipe/product grade UX).
 *
 * A "grade" here is a LABEL ONLY — the cast-iron grade a heat is run to (FG260,
 * GJS-450, …) with its route (grey / ductile), the standard that names it, and
 * common aliases other standards use for the same grade. There is deliberately
 * NO chemistry on this surface: the catalog identifies grades, it never derives
 * or asserts composition/mechanical properties from them. The picker built on it
 * (GradePicker) always keeps a free-text escape hatch so a plant can run a grade
 * the catalog doesn't list.
 *
 * Mirrors the typed-wrapper shape of twin-recipes.ts / twin-products.ts:
 * snake_case wire shapes, the shared `api` client, a stable query key, and the
 * same build-flag + authenticated/org-resolved live gate. Read-only: the catalog
 * is foundry-pack seed data, not org-authored, so there are no mutations here.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

/**
 * One catalog grade. LABEL ONLY — `grade` is the canonical name, `route` is the
 * cast-iron family ("grey" | "ductile"; typed as string so a future route never
 * breaks the client), `standard` is the naming standard (e.g. "IS 210"), and
 * `aliases` are equivalent names other standards use for the same grade. No
 * chemistry is carried or implied.
 */
export interface Grade {
  grade: string;
  route: string;
  standard: string;
  aliases?: string[];
}

/** `GET /v1/twin/grades` response — the label catalog plus its count. */
export interface GradesResponse {
  grades: Grade[];
  count: number;
}

export const twinGrades = {
  /** The foundry-pack grade catalog (labels only — no chemistry). */
  list: () => api.get<GradesResponse>("/v1/twin/grades"),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApi = import.meta.env.VITE_USE_LIVE_API === "true";

/**
 * Live fetches require the build flag AND an authenticated, org-resolved
 * session — same gate as the sibling twin-* modules (kept local so this module
 * stays additive and free of a circular import on queries.ts).
 */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApi && isAuthenticated && activeOrgId !== null;
}

/** Query key for the grade catalog — exported for symmetry with the twin-* modules. */
export const TWIN_GRADES_KEY = ["twin-grades"] as const;

/**
 * The grade catalog for the active org (drives GradePicker). Stub mode / an
 * unresolved session simply never fetches, so `data` is undefined and callers
 * read `data?.grades ?? []` → an empty catalog (the picker's free-text escape
 * hatch still works with zero grades). Cached long: the catalog is static
 * foundry-pack seed data, not org-mutable.
 */
export function useGradesQuery() {
  return useQuery({
    queryKey: TWIN_GRADES_KEY,
    queryFn: () => twinGrades.list(),
    enabled: useLiveEnabled(),
    staleTime: 5 * 60_000,
  });
}
